// @fluxora/auth — shared context + guards.
//
// A SINGLE AuthContext is fed by either provider (dummy or real OIDC), so
// consumers are provider-agnostic. `useAuth` / `RequireAuth` / `RequireRole`
// all read from here.

import {
  createContext,
  useContext,
  type ReactElement,
  type ReactNode,
} from 'react';

import type { UserRole } from '@fluxora/types';

import type { AuthContextValue } from './types.js';

/** Shared context. `null` when no provider is mounted (a programmer error). */
export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Read the active auth context. Throws if no provider is mounted.
 *
 * US LoginPage uses `const { login, state } = useAuth()`; `login` is
 * positional `(email, password)` and `state` is the user-or-null.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an <AuthProvider> or <DummyAuthProvider>');
  }
  return ctx;
}

/** True once the active provider is authenticated. */
export function useIsAuthenticated(): boolean {
  return useAuth().isAuthenticated;
}

/** False until the active provider has finished its initial session restore. */
export function useIsAuthHydrated(): boolean {
  return useAuth().isHydrated;
}

export interface RequireAuthProps {
  children: ReactNode;
  /**
   * Rendered while the provider is hydrating (instead of treating the user
   * as unauthenticated). The US app passes its own spinner via its local
   * RequireAuth wrapper, but package-level guards accept a fallback too.
   */
  fallback?: ReactNode;
  /** Rendered when the user is not authenticated (after hydration). */
  unauthenticated?: ReactNode;
}

/**
 * Gate children behind authentication. During hydration renders `fallback`
 * (default: nothing) rather than the unauthenticated branch, so a reload does
 * not flash-redirect before the session is restored.
 *
 * Note: the US app ships its own router-aware `RequireAuth` (it needs
 * `<Navigate>`); this package-level guard is the routing-agnostic version the
 * stub exported, kept for non-US consumers and back-compat.
 */
export function RequireAuth({
  children,
  fallback = null,
  unauthenticated = null,
}: RequireAuthProps): ReactElement {
  const { isHydrated, isAuthenticated } = useAuth();
  if (!isHydrated) return <>{fallback}</>;
  if (!isAuthenticated) return <>{unauthenticated}</>;
  return <>{children}</>;
}

export interface RequireRoleProps {
  role: UserRole | UserRole[];
  children: ReactNode;
  /** Rendered when the user lacks the required role. Defaults to nothing. */
  forbidden?: ReactNode;
}

/**
 * Gate children behind one or more {@link UserRole}s. The user passes if they
 * hold ANY of the required roles. Renders `forbidden` otherwise.
 */
export function RequireRole({
  role,
  children,
  forbidden = null,
}: RequireRoleProps): ReactElement {
  const { user } = useAuth();
  const required = Array.isArray(role) ? role : [role];
  const granted = user?.roles ?? [];
  const ok = required.some((r) => granted.includes(r));
  if (!ok) return <>{forbidden}</>;
  return <>{children}</>;
}
